import { getRuntimeEnvironmentFromProjectId, isSupportedRuntimeEnvironment } from '#shared/constants/firebase'
import { useExponea } from '@/composables/exponea/useExponea'
import { useApplicationStore } from '@/stores/application'
import { logger } from '@/utils/logger'
import Cookies from 'js-cookie'

const EXPONEA_COOKIE_NAME = '__exponea_etc__'
const COOKIE_POLL_INTERVAL_MS = 1000
const COOKIE_POLL_TIMEOUT_MS = 15000

type ExponeaCustomerPayload = {
    customerIds: {
        registered: string
        registered_email?: string
        cookie?: string
    }
    customerProperties: {
        email?: string
        phone?: string
        firstName?: string
        lastName?: string
    }
}

function normalizeCustomerProperty(value?: string | number | null): string | undefined {
    if (typeof value !== 'string' && typeof value !== 'number') {
        return undefined
    }

    const normalized = String(value).trim()
    return normalized ? normalized : undefined
}

function buildExponeaCustomerPayload(store: ReturnType<typeof useApplicationStore>): ExponeaCustomerPayload | null {
    if (!store.isReady || !store.userId || !store.userProfile) {
        return null
    }

    const customerIds: ExponeaCustomerPayload['customerIds'] = {
        registered: store.userId
    }
    const exponeaCookie = Cookies.get(EXPONEA_COOKIE_NAME)

    if (store.userRegisteredEmail) {
        customerIds.registered_email = store.userRegisteredEmail
    }

    if (exponeaCookie) {
        customerIds.cookie = exponeaCookie
    }

    return {
        customerIds,
        customerProperties: {
            email: normalizeCustomerProperty(store.userPrivateProfile?.contactEmail),
            phone: normalizeCustomerProperty(store.userPrivateProfile?.phone),
            firstName: normalizeCustomerProperty(store.userProfile.firstName),
            lastName: normalizeCustomerProperty(store.userProfile.lastName)
        }
    }
}

function getPayloadSignature(payload: ExponeaCustomerPayload | null): string | null {
    return payload ? JSON.stringify(payload) : null
}

export default defineNuxtPlugin({
    setup() {
        if (!import.meta.client) {
            return
        }

        const runtimeConfig = useRuntimeConfig()
        const runtimeEnvironment = getRuntimeEnvironmentFromProjectId(runtimeConfig.public.FIREBASE_PROJECT_ID)

        if (!isSupportedRuntimeEnvironment(runtimeEnvironment)) {
            return
        }

        const store = useApplicationStore()
        const { updateExponeaUser } = useExponea()

        let lastSyncedSignature: string | null = null
        let syncInFlight = false
        let queuedSync = false
        let cookiePollStartedAt = 0
        let cookiePollTimer: ReturnType<typeof setInterval> | null = null

        const stopCookiePolling = () => {
            if (!cookiePollTimer) {
                return
            }

            clearInterval(cookiePollTimer)
            cookiePollTimer = null
            cookiePollStartedAt = 0
        }

        const resetSyncState = () => {
            lastSyncedSignature = null
            syncInFlight = false
            queuedSync = false
            stopCookiePolling()
        }

        const syncCustomer = async () => {
            const payload = buildExponeaCustomerPayload(store)
            const signature = getPayloadSignature(payload)

            if (!payload || !signature) {
                if (!store.userId) {
                    resetSyncState()
                }
                return
            }

            if (signature === lastSyncedSignature) {
                return
            }

            if (syncInFlight) {
                queuedSync = true
                return
            }

            syncInFlight = true

            try {
                await updateExponeaUser(payload)
                lastSyncedSignature = signature
            } catch (error) {
                logger.error('[Exponea Customer Sync] Failed to sync customer:', error)
            } finally {
                syncInFlight = false

                if (queuedSync) {
                    queuedSync = false
                    await syncCustomer()
                }
            }
        }

        const ensureCookieBackfillSync = () => {
            if (!store.isReady || !store.userId || Cookies.get(EXPONEA_COOKIE_NAME) || cookiePollTimer) {
                return
            }

            cookiePollStartedAt = Date.now()
            cookiePollTimer = setInterval(() => {
                if (!store.userId || !store.isReady) {
                    stopCookiePolling()
                    return
                }

                if (Cookies.get(EXPONEA_COOKIE_NAME)) {
                    stopCookiePolling()
                    void syncCustomer()
                    return
                }

                if (Date.now() - cookiePollStartedAt >= COOKIE_POLL_TIMEOUT_MS) {
                    stopCookiePolling()
                }
            }, COOKIE_POLL_INTERVAL_MS)
        }

        watch(
            () => [
                store.isReady,
                store.userId ?? null,
                store.userRegisteredEmail ?? null,
                store.userProfile?.firstName ?? null,
                store.userProfile?.lastName ?? null,
                store.userPrivateProfile?.contactEmail ?? null,
                store.userPrivateProfile?.phone ?? null
            ],
            ([isReady, userId]) => {
                if (!isReady || !userId) {
                    resetSyncState()
                    return
                }

                void syncCustomer()
                ensureCookieBackfillSync()
            },
            { immediate: true }
        )
    }
})
